import React, { useState } from "react";
import { useNode } from "@craftjs/core";
import { FormControl, FormLabel, TextField, Select, MenuItem, Slider } from "@mui/material";
import { useTextPersistence } from "qwanyx";

export const CopyButton = ({
 name,
 label,
 qwantumId,
 backgroundColor = '#f5f5f5',
 color = '#000000',
 borderRadius = 4,
 padding = 8,
 fontSize = 14,
 fontFamily = 'Arial',
 position = 'relative',
 top,
 left,
 right,
 bottom,
 unit = 'px',
 lockedEdges = { top: false, left: false, right: false, bottom: false },
 cssClass,
 hidden,
 style,
 ...props
}) => {
 const { 
   connectors: { connect, drag }, 
   isSelected,
   isHovered,
 } = useNode((state) => ({
   isSelected: state.events.selected,
   isHovered: state.events.hovered,
 }));

 // Get the persisted value - only read, never write
 const { text: persistedText } = useTextPersistence(qwantumId);
 const [copied, setCopied] = useState(false);

 if (hidden) return null;

 const handleCopy = async () => {
   if (persistedText) {
     try {
       await navigator.clipboard.writeText(persistedText);
       setCopied(true);
       setTimeout(() => setCopied(false), 2000);
     } catch (err) {
       console.error('Failed to copy:', err);
     }
   }
 };

 const getSelectionStyle = () => {
   if (isSelected) {
     return {
       outline: '1px dashed #2196f3',
       outlineOffset: '2px'
     };
   }
   if (isHovered) {
     return {
       outline: '1px dashed #ff9800',
       outlineOffset: '1px'
     };
   }
   return {};
 };

 const getPositionStyle = () => {
   if (position === 'absolute' || position === 'relative') {
     const positionStyle = { position };
     
     if (lockedEdges.top && top !== undefined) {
       positionStyle.top = `${top}${unit}`;
     }
     if (lockedEdges.left && left !== undefined) {
       positionStyle.left = `${left}${unit}`;
     }
     if (lockedEdges.right && right !== undefined) {
       positionStyle.right = `${right}${unit}`;
     }
     if (lockedEdges.bottom && bottom !== undefined) {
       positionStyle.bottom = `${bottom}${unit}`;
     }
     
     return positionStyle;
   }
   return { position };
 };

 const getButtonText = () => {
   if (copied) return 'Copied!';
   if (label) return label;
   return persistedText || qwantumId || 'No ID';
 };

 return (
   <button
     ref={ref => connect(drag(ref))}
     name={name}
     className={cssClass}
     onClick={handleCopy}
     style={{
       backgroundColor: copied ? '#4caf50' : backgroundColor,
       color: copied ? '#ffffff' : color,
       border: 'none',
       borderRadius: `${borderRadius}px`,
       padding: `${padding}px`,
       fontSize: `${fontSize}px`,
       fontFamily: fontFamily,
       cursor: 'pointer',
       transition: 'all 0.2s ease',
       display: 'inline-block',
       ...getPositionStyle(),
       ...style,
       ...getSelectionStyle()
     }}
     {...props}
   >
     {getButtonText()}
   </button>
 );
};

const CopyButtonSettings = () => {
 const { 
   actions: { setProp }, 
   name, label, qwantumId, backgroundColor, color, borderRadius, padding, fontSize, fontFamily,
   position, top, left, right, bottom, unit, lockedEdges, cssClass
 } = useNode((node) => ({
   name: node.data.props.name,
   label: node.data.props.label,
   qwantumId: node.data.props.qwantumId,
   backgroundColor: node.data.props.backgroundColor,
   color: node.data.props.color,
   borderRadius: node.data.props.borderRadius,
   padding: node.data.props.padding,
   fontSize: node.data.props.fontSize,
   fontFamily: node.data.props.fontFamily,
   position: node.data.props.position,
   top: node.data.props.top,
   left: node.data.props.left,
   right: node.data.props.right,
   bottom: node.data.props.bottom,
   unit: node.data.props.unit,
   lockedEdges: node.data.props.lockedEdges,
   cssClass: node.data.props.cssClass,
 }));

 return (
   <div>
     <FormControl fullWidth margin="normal">
       <FormLabel>Component Name</FormLabel>
       <TextField
         value={name || ''}
         onChange={(e) => setProp(props => props.name = e.target.value)}
         size="small"
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Quantum ID</FormLabel>
       <TextField
         value={qwantumId || ''}
         onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
         size="small"
         placeholder="Enter quantum ID to copy its stored value"
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Button Label</FormLabel>
       <TextField
         value={label || ''}
         onChange={(e) => setProp(props => props.label = e.target.value)}
         size="small"
         placeholder="Leave empty to show the persisted value"
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Background Color</FormLabel>
       <input
         type="color"
         value={backgroundColor || '#f5f5f5'}
         onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Text Color</FormLabel>
       <input
         type="color"
         value={color || '#000000'}
         onChange={(e) => setProp(props => props.color = e.target.value)}
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Font Size</FormLabel>
       <Slider
         value={fontSize || 14}
         step={1}
         min={8}
         max={32}
         valueLabelDisplay="auto"
         onChange={(_, value) => setProp(props => props.fontSize = value)}
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Font Family</FormLabel>
       <Select
         value={fontFamily || 'Arial'}
         onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
         size="small"
       >
         <MenuItem value="Arial">Arial</MenuItem>
         <MenuItem value="Helvetica">Helvetica</MenuItem>
         <MenuItem value="Montserrat">Montserrat</MenuItem>
       </Select>
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Border Radius</FormLabel>
       <Slider
         value={borderRadius || 4}
         step={1}
         min={0}
         max={20}
         valueLabelDisplay="auto"
         onChange={(_, value) => setProp(props => props.borderRadius = value)}
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>Padding</FormLabel>
       <Slider
         value={padding || 8}
         step={1}
         min={0}
         max={30}
         valueLabelDisplay="auto"
         onChange={(_, value) => setProp(props => props.padding = value)}
       />
     </FormControl>

     <FormControl fullWidth margin="normal">
       <FormLabel>CSS Class</FormLabel>
       <TextField
         value={cssClass || ''}
         onChange={(e) => setProp(props => props.cssClass = e.target.value)}
         size="small"
       />
     </FormControl>
   </div>
 );
};

CopyButton.craft = {
 props: {
   name: "",
   label: "",
   qwantumId: "",
   backgroundColor: '#f5f5f5',
   color: '#000000',
   borderRadius: 4,
   padding: 8,
   fontSize: 14,
   fontFamily: 'Arial',
   position: 'relative',
   top: 0,
   left: 0,
   right: 0,
   bottom: 0,
   unit: 'px',
   lockedEdges: { top: false, left: false, right: false, bottom: false },
   cssClass: ''
 },
 related: {
   settings: CopyButtonSettings
 }
};